import { StorageAccountState } from 'mbs-ui-shared@models/StorageAccount';
import { BuildType } from './BuildType.enum';
import { DeepPartial } from './DeepPartial';
import { LicenseType } from './LicenseType.enum';
import { NotificationCustom } from './Notification';
import { QueryPagingParams, QuerySortParams } from './Paging';
import { StorageType } from './StorageType.enum';

export enum LicensesSettings {
  Custom = 0,
  GlobalPool = 1,
  CompanyPool = 2
}

export enum LimitsMode {
  Unlimited,
  CompanyLimit
}

export enum LicensesLimitsSettings {
  NoLimits,
  CompanyLimits
}

export enum LicensesPoolType {
  GlobalPool = 0,
  UserPool = 1
}

export enum EditionsSettings {
  None = -1,
  All = 0,
  Specific = 1,
  OnlyAllowed = 2
}

export class LicensesGranted {
  licenseType: LicenseType;
  activatedCount?: number;
  trialCount?: number;
  available?: number;
  granted: number;
}

export class CompanyDestination {
  bucketId: string;
  bucketName: string;
  displayDestinationName: string;
  displayStorageName: string;
  id?: string;
  path?: string;
  region?: string;
  regionDisplayName?: string;
  storageAccountId: string;
  storageAccountState?: StorageAccountState;
  storageAccountType: StorageType;
  storageLimitId?: string;
  storageLimitName?: string;
  storageLimitVolume?: number;
  storageTotal?: number;
  storageUsed?: number;
}

export enum AuthType {
  LOGIN_PASS,
  AD_BRIDGE,
  AD_DIRECT
}

export interface AllowedLicense {
  licenseType: LicenseType;
  isAllow: boolean;
}

export interface Limit {
  licenseType: LicenseType;
  assigned: number;
  limit: number;
}

export interface CompanyPoolSetting {
  licenseType: LicenseType;
  assigned: number;
  companyPool: number;
  globalPool: number;
}

interface AssignLicensesFrom {
  mode: LicensesSettings;
  companyPoolSettings: Array<CompanyPoolSetting>;
}

export interface LimitSettings {
  mode: number;
  limits: Array<Limit>;
}

export interface PoolSettingsFromForm {
  companyPool?: unknown;
  globalPool?: unknown;
}

export interface LicenseSettings {
  allowedLicenses: Array<AllowedLicense>;
  defaultLicense: LicenseType;
  assignLicensesFrom: AssignLicensesFrom;
  limitSettings: LimitSettings;
}

export enum BandwidthSelectionType {
  All = 0,
  WithTags = 1,
  WithOutTags = 2
}

export interface BandwidthSettings {
  cloudSpeedLimit: number;
  localSpeedLimit: number;
  selectionType: BandwidthSelectionType;
  tags: number[];
}

export default class Company {
  administrators: string[] = [];
  agentSettingsId: string;
  availableEditions = EditionsSettings.All;
  backupDestinationsCount?: number;
  backupO365NotificationsOn: boolean;
  bandwidthSettings?: BandwidthSettings;
  defaultCompany = false;
  defaultDestinations: CompanyDestination[] = [];
  defaultEdition = BuildType.NA;
  id: string;
  inheritedDestinations: CompanyDestination[] = [];
  licenseSettings: DeepPartial<LicenseSettings>;
  licensesGranted: LicensesGranted[] = [];
  licensesSettings = LicensesSettings.GlobalPool;
  name?: string;
  notificationSettings: NotificationCustom = new NotificationCustom();
  restoreO365NotificationsOn: boolean;
  specificEditions: BuildType[];
  storageUsageApps: number;
  storageUsageBackup: number;
  storageLimit = -1;

  readonly computersCount: number;
  readonly licensesCount: number;
  readonly storageUsage: number;
  readonly usersCount: number;

  disabled?: boolean; // internal property
}

export type CompaniesFilters = {
  ids?: string[];
  search?: string;
  licensePoolType?: number;
};

export enum CompaniesAPIFilterKeys {
  Search = 'search',
  LicensePoolType = 'licensePoolType'
}

export type CompaniesSortFiltersParams = Partial<QuerySortParams> & CompaniesFilters;
export type CompaniesQueryParams = CompaniesSortFiltersParams & Partial<QueryPagingParams> & { ids?: string[] };
